import { Box, Grid } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch, toBuddhistYear } from 'utils/app.utils'

/**COMPONENT */
import Loading from 'component/Loading'

/**STYLE */

import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewDatePicker, { PICKER_VIEWS } from 'component/Input/InputSecondNewDatePicker'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import RadioButton from 'new-components/buttons/radio-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import { UseSnCode } from 'utils/useSnCode'
import DfLabList from './DfList'
import { ExportDfToExcel, ReportData } from './ExportDfToExcel'



type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
  schedule: number | null
}



export default function Df() {
  const { t } = useTranslation()
  const history = useHistory()
  const momentNow = moment().format('YYYY-MM-DD')
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [fetch, setFetch] = useState(false)
  const [dfData, getDfData] = useState<any[]>([])
  const [date, setDate] = useState<string>(momentNow)
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [dispay, setDisplay] = useState('day')
  const [loading, setLoading] = useState<boolean>(true)
  const [selectDoctors, setSelectDoctors] = useState<any[]>([''])
  const [doctorLength, setDoctorLength] = useState<any>()
  const [doctorSchedule, setDoctorSchedule] = useState<any[]>([])
  const [snType, setSnType] = useState(false)

  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode()
      setSnType(sn)
    }
    fetchData()
  }, [snType])

  useEffect(() => {
    handleSnCode()
  }, [])

  const getAllFilter = () => {
    return [
      {
        label: t('REPORT.FILTER.BRANCH'),
        value: _.get(_.find(branches, { branchId }), 'branchName') || _.get(_.find(branches, { branchId }), 'branchNameEn')
      },
      {
        label: dispay === 'day' ? t('REPORT.FILTER.MONTH') : t('REPORT.FILTER.DAY'),
        value: dispay === 'day' ? toBuddhistYear(moment(date), 'DD/MM/YYYY') : toBuddhistYear(moment(date), 'MMMM YYYY')
      },
      {
        label: t('REPORT.FILTER.DOCTOR'),
        value: getFilterUser()
      }
    ]
  }

  const getFilterUser = () => {
    if (doctors.length && doctors.every((d: any) => selectDoctors.includes(d.userId))) {
      return ` ${t('REPORT.REPORT_GROUP.ALL')}`
    }
    return ` ${doctors
      .filter((d: any) => selectDoctors.includes(d.userId))
      ?.map((d: any) => d.fullname)
      .join(', ')}`
  }

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'DF') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })


    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [branchId])

  const fetchDoctor = useCallback(async () => {
    ClinicApi.findAllDoctors(date, dispay, date, date)
      .then((resp) => {
        setDoctorLength(resp.data.length)
        setDoctors([{ userId: 999999, firstname: '', lastname: '', fullname: t('REPORT.TITLE.NO_DOCTOR'), schedule: null }, ...resp.data])
        setDoctorSchedule(_.map(_.filter(resp.data, (d) => d.userId === d.schedule), (d: any) => d.userId))
        setSelectDoctors(['DOCTOR_SCHEDULE', 999999, ...resp.data.map((d: any) => d.userId)])
      })
      .catch((e) => {
        return
      })
  }, [])


  const fetchDF = useCallback(async () => {
    let condition: any = {
      page: page,
      pageLimit: pageLimit,
      branchId: branchId,
      date: date,
      dispay: dispay
    }
    if (doctors) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: _.filter(selectDoctors, (v: any) => v > 0).join(',') }
    }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    ReportApi.reportDf(condition)
      .then(({ headers, data }) => {
        getDfData(data)
        setRowCount(headers['x-total'])
      })
      .catch((e) => setLoading(false))
      .finally(() => {
        setLoading(false)
        setFetch(false)
      })
  }, [page, pageLimit, sortBy, sortType, fetch])


  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

  const handleChangePage = (val: number) => setPage(val)


  useEffect(() => {
    fetchDoctor()
  }, [fetchDoctor])

  useEffect(() => {
    fetchDF()
  }, [fetchDF])


  const handleExportExcel = async () => {
    const data: ReportData = {
      branchName: `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')} `,
      date: date,
      doctorFullname: getFilterUser(),
      display: dispay === 'day' ? 'day' : 'month',
      dfData: dfData,
      t: t,
    }

    try {
      await ExportDfToExcel(data)
    } catch (err) {
      console.error('Export to Excel failed:', err)
    }
  }

  return (
    <Box sx={{ height: '100%' }}>
      <HeaderCard text={t('REPORT.REPORT_NAME.DF_REPORT')} />
      <Box sx={{ px: 3, pt: 2 }}>
        <RadioButton
          name=""
          onChange={e => {
            setDisplay(e.target.value);
          }} options={[
            { label: t('REPORT.MONTH.DAYLY'), value: 'day' },
            { label: t('REPORT.MONTH.MONTHLY'), value: 'month' }
          ]}
          row
          defaultValue="day"
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => {
                setBranchId(Number(e.target.value));
                setPage(1);
              }}
              options={branches.map(b => ({
                value: b.branchId,
                name: b.branchName || b.branchNameEn || ''
              }))}
              width="100%"
            />
          </Grid>

          {dispay === 'day' && (
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <InputSecondNewDatePicker
                dateFormat={'DD/MM/YYYY'}
                placeholder={t('REPORT.FILTER.DATE')}
                label={''}
                inputHeight={40}
                value={date}
                onchange={(e: any) => {
                  setDate(moment(e).format('YYYY-MM-DD'))
                  setPage(1)
                }}
                views={PICKER_VIEWS.DAY || PICKER_VIEWS.MONTH || PICKER_VIEWS.YEAR}
              />
            </Grid>
          )}

          {dispay === 'month' && (
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <InputSecondNewDatePicker
                dateFormat={`MMMM YYYY`}
                placeholder={t('REPORT.FILTER.DATE')}
                label={''}
                inputHeight={40}
                value={date}
                onchange={(e: any) => {
                  setDate(moment(e).format('YYYY-MM-DD'))
                  setPage(1)
                }}
                views={PICKER_VIEWS.MONTH}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('USER_DOCTOR')}
              options={[
                { id: 'DOCTOR_SCHEDULE', name: t('REPORT.FILTER.DOCTOR_SCHEDULE') },
                ...doctors.map((d: any) => ({ id: d.userId, name: d.fullname })),
              ]}
              selected={selectDoctors}
              onChange={(ids: any) => {
                if (ids.includes('DOCTOR_SCHEDULE') && ids.length === 1) {
                  const currentSchedule = doctorSchedule.every((id) => selectDoctors.includes(id));
                  const newSelected = new Set(selectDoctors);
                  if (currentSchedule) {
                    doctorSchedule.forEach((id) => newSelected.delete(id));
                  } else {
                    doctorSchedule.forEach((id) => newSelected.add(id));
                  }
                  setSelectDoctors(['DOCTOR_SCHEDULE', ...Array.from(newSelected)]);
                  return;
                }
                setSelectDoctors(ids)
              }}
              fullWidth
              width="100%"
              searchable
              searchPlaceholder={t('REPORT.FILTER.DOCTOR_SEARCH_PLACEHOLDER')}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} xl={6}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <MainButton
                title={t('INPUT.SEARCH')}
                onClick={() => {
                  setFetch(true)
                  setPage(1)
                  setLoading(true)
                }}
                type={'primary'}
                variant={'contained'}
                style={{ width: '120px' }}
              />
              <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                if (e === 'Excel') handleExportExcel()
                else if (e === 'PDF') {
                  const style = document.createElement("style");
                  style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                  document.head.appendChild(style);
                  window.print();
                  document.head.removeChild(style);

                }
              }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, px: { xs: 1, xl: 3 }, pb: 4 }}>
          <DfLabList
            filterTable={getAllFilter()}
            page={page}
            pageLimit={pageLimit}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={dfData}
            date={date}
            dispay={dispay}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
          />
        </Box>
      </Box>
      <Loading show={loading} type="fullLoading" />
    </Box >
  );
}

